<template>
  <div class="container-fluid p-4">
    <div class="row mb-4">
      <div class="col-12 d-flex align-items-center justify-content-between">
        <h2 class="mb-0 fw-bold gradient-text">Challenges</h2>
        <button type="button" class="btn btn-primary" @click="refresh">
          <i class="bi bi-arrow-clockwise me-2"></i>Refresh
        </button>
      </div>
    </div>

    <!-- Tabs -->
    <div class="row mb-3">
      <div class="col-12">
        <div class="nav-pills">
          <button class="nav-pill" :class="{ active: activeTab === 'my' }" @click="activeTab = 'my'">
            <i class="bi bi-person-check me-1"></i> My Challenges
          </button>
          <button class="nav-pill" :class="{ active: activeTab === 'available' }" @click="activeTab = 'available'">
            <i class="bi bi-list-task me-1"></i> Available
          </button>
        </div>
      </div>
    </div>

    <!-- My Challenges -->
    <div v-if="activeTab === 'my'" class="row g-3">
      <div class="col-md-4" v-for="ch in joinedChallenges" :key="ch.id">
        <div class="modern-card p-3 h-100 d-flex flex-column">
          <div class="d-flex align-items-start justify-content-between mb-2">
            <div>
              <h5 class="fw-bold mb-1">{{ ch.title }}</h5>
              <small class="text-muted">{{ ch.duration }} • {{ ch.difficulty }}</small>
            </div>
            <i :class="ch.icon || 'bi bi-trophy'" style="font-size:1.5rem;color:var(--role-primary)"></i>
          </div>
          <p class="text-secondary flex-grow-1">{{ ch.description }}</p>
          <div class="d-flex align-items-center justify-content-between">
            <div class="me-3">
              <small class="text-muted d-block">Progress</small>
              <span class="fw-bold">{{ ch.progress ?? 0 }}%</span>
            </div>
            <button type="button" class="btn btn-primary" @click="viewDetails(ch.id)">
              View Details
            </button>
          </div>
        </div>
      </div>
      <div class="col-12" v-if="!loading && joinedChallenges.length === 0">
        <div class="alert alert-warning">You haven't joined any challenges yet. Explore the Available tab to get started.</div>
      </div>
    </div>

    <!-- Available Challenges -->
    <div v-else class="row g-3">
      <div class="col-md-4" v-for="ch in availableChallenges" :key="ch.id">
        <div class="modern-card p-3 h-100 d-flex flex-column">
          <div class="d-flex align-items-start justify-content-between mb-2">
            <div>
              <h5 class="fw-bold mb-1">{{ ch.title }}</h5>
              <small class="text-muted">{{ ch.duration }} • {{ ch.difficulty }}</small>
            </div>
            <i :class="ch.icon || 'bi bi-trophy'" style="font-size:1.5rem;color:var(--role-primary)"></i>
          </div>
          <p class="text-secondary flex-grow-1">{{ ch.description }}</p>
          <div class="d-flex gap-2 justify-content-end">
            <button type="button" class="btn btn-outline-primary" @click="viewDetails(ch.id)">Details</button>
            <button type="button" class="btn btn-success" @click="join(ch)">Join</button>
          </div>
        </div>
      </div>
      <div class="col-12" v-if="!loading && availableChallenges.length === 0">
        <div class="alert alert-info">No challenges available this week.</div>
      </div>
    </div>
  </div>
  
</template>

<script>
export default {
  name: 'ChallengesPage',
  data() {
    return {
      loading: false,
      activeTab: 'my',
      joinedChallenges: [],
      allChallenges: []
    }
  },
  computed: {
    currentUser() {
      const stored = localStorage.getItem('currentUser')
      return stored ? JSON.parse(stored) : null
    },
    availableChallenges() {
      const joinedIds = new Set(this.joinedChallenges.map(c => c.id))
      return (this.allChallenges || []).filter(c => !joinedIds.has(c.id))
    }
  },
  async mounted() {
    await this.refresh()
  },
  methods: {
    async refresh() {
      try {
        this.loading = true
        const { getUserChallenges, getAllChallengesForUser, browseChallenges } = await import('../services/challengeService.js')
        const userId = this.currentUser?.id
        const [joinedRes, allRes, browseRes, browseAllRes] = await Promise.all([
          getUserChallenges(userId),
          getAllChallengesForUser(userId),
          browseChallenges({ status: 'Active', user_id: userId }),
          // Fallback in case status filter yields nothing
          browseChallenges({ user_id: userId })
        ])
        this.joinedChallenges = Array.isArray(joinedRes?.data) ? joinedRes.data : (Array.isArray(joinedRes) ? joinedRes : [])
        const allA = Array.isArray(allRes?.data) ? allRes.data : (Array.isArray(allRes) ? allRes : [])
        const browsed = Array.isArray(browseRes?.data) ? browseRes.data : (Array.isArray(browseRes) ? browseRes : [])
        const browsedAll = Array.isArray(browseAllRes?.data) ? browseAllRes.data : (Array.isArray(browseAllRes) ? browseAllRes : [])
        // Merge unique by id
        const map = new Map()
        ;[...allA, ...browsed, ...browsedAll].forEach(c => { if (c?.id) map.set(c.id, c) })
        this.allChallenges = Array.from(map.values())
      } catch (e) {
        console.error('Failed to load challenges:', e)
        this.joinedChallenges = []
        this.allChallenges = []
      } finally {
        this.loading = false
      }
    },
    viewDetails(id) {
      if (id) this.$router.push(`/challenges/${id}`)
    },
    async join(challenge) {
      try {
        const userId = this.currentUser?.id
        const { joinChallenge } = await import('../services/challengeService.js')
        await joinChallenge(challenge.id, userId)
        await this.refresh()
        this.$router.push(`/challenges/${challenge.id}`)
      } catch (e) {
        console.error('Join failed:', e)
        this.showFeedback('Could not join the challenge. Please try again.', 'error')
      }
    },

    showFeedback(message, type = 'info') {
      // Create feedback alert
      const alertClass = type === 'success' ? 'alert-success' : 
                         type === 'warning' ? 'alert-warning' :
                         type === 'error' ? 'alert-danger' : 'alert-info';
      const alert = document.createElement('div');
      alert.className = `alert ${alertClass} alert-dismissible fade show position-fixed`;
      alert.style.cssText = 'top: 20px; right: 20px; z-index: 9999; min-width: 300px;';
      alert.innerHTML = `
        <strong>${message}</strong>
        <button type="button" class="btn-close" onclick="this.parentElement.remove()"></button>
      `;
      document.body.appendChild(alert);
      
      // Auto-remove after 5 seconds
      setTimeout(() => {
        if (alert.parentElement) {
          alert.remove();
        }
      }, 5000);
    }
  }
}
</script>

<style scoped>
.nav-pills { display:flex; gap:.5rem; background:var(--bg-primary); padding:.5rem; border-radius:12px; border:1px solid var(--border-color); }
.nav-pill { padding:.5rem 1rem; border:none; background:transparent; border-radius:8px; cursor:pointer; color:var(--text-secondary); }
.nav-pill.active { background:var(--role-primary); color:#fff; }
.modern-card { background: var(--bg-primary); border:1px solid var(--border-color); border-radius:12px; box-shadow: var(--shadow); }
.gradient-text { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
</style>


